import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBhajanDataRequest, selectCatalog, selectStatus } from '../Features/BhajanSlice';
import type { AppDispatch } from '../store/store';
import { EmptyState, Skeleton } from '../ui';
import { bhajanToCardData } from './BhajanCard';
import { fetchPlaylist, type PlaylistResponse } from '../services/api';
import { AppDownload } from './layout/AppDownload';
import type { AnyGraphNode } from '../types/graphNodes';

const BHAJAN_NODE_TYPE_ID = 1;

// Native app deep-link config (BhajanMala).
const APP_SCHEME = 'bm';
const ANDROID_PACKAGE = 'org.dycusa.bhajanmala';
const APP_STORE_URL = 'https://apps.apple.com/app/id583564306';
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

/**
 * Force-open the playlist in the native app, with a graceful store fallback.
 * Per platform (researched): the page IS the Universal Link target, so the
 * https URL can't reopen the app — we must use the custom scheme / intent URL.
 *  - Android: an `intent://` URL — Chrome opens the app or auto-redirects to
 *    Play Store via S.browser_fallback_url.
 *  - iOS: fire the custom scheme; custom schemes have NO built-in fallback, so
 *    after a short delay send the user to the App Store IF the page is still
 *    visible (i.e. the app didn't open). Must run from a user gesture (onClick).
 *  - Desktop/other: no app — go to the App Store listing.
 */
function openPlaylistInApp(playlistId: string): void {
  const path = `playlists/${encodeURIComponent(playlistId)}`;
  const ua = navigator.userAgent || '';
  const isAndroid = /android/i.test(ua);
  // Apple = iPhone/iPad/iPod, iPadOS-in-desktop-mode, AND macOS (the Mac
  // Catalyst build registers the bm:// scheme too).
  const isApple = /iphone|ipad|ipod/i.test(ua) || /macintosh|mac os x/i.test(ua);

  if (isAndroid) {
    const fallback = encodeURIComponent(PLAY_STORE_URL);
    window.location.href =
      `intent://${path}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
    return;
  }

  if (isApple) {
    // Custom schemes have no built-in fallback. After a short delay, send the
    // user to the App Store UNLESS the app took over. Detect that via several
    // signals: iOS backgrounds the tab (visibilitychange/pagehide); macOS keeps
    // the tab visible but the window loses focus (blur).
    let opened = false;
    const cancel = () => { opened = true; window.clearTimeout(timer); };
    const timer = window.setTimeout(() => {
      if (!opened && !document.hidden) window.location.href = APP_STORE_URL;
    }, 1500);
    window.addEventListener('blur', cancel, { once: true });
    window.addEventListener('pagehide', cancel, { once: true });
    document.addEventListener('visibilitychange', () => { if (document.hidden) cancel(); }, { once: true });
    window.location.href = `${APP_SCHEME}://${path}`;
    return;
  }

  // Windows/Linux/other — no native app; offer the App Store listing.
  window.location.href = APP_STORE_URL;
}

const Playlist: React.FC = () => {
  const { playlistId } = useParams<{ playlistId?: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const catalog = useSelector(selectCatalog);
  const status = useSelector(selectStatus);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchBhajanDataRequest());
  }, [dispatch, status]);

  // Fetch the playlist itself (name + ordered bhajan node ids) from the API.
  const [playlist, setPlaylist] = useState<PlaylistResponse | null>(null);
  const [playlistState, setPlaylistState] = useState<'loading' | 'ready' | 'notfound' | 'error'>('loading');
  useEffect(() => {
    if (!playlistId) return;
    let cancelled = false;
    setPlaylistState('loading');
    fetchPlaylist(playlistId)
      .then((p) => {
        if (cancelled) return;
        // A missing playlist can come back as the SPA HTML (CloudFront rewrites
        // API 404s to index.html), so validate the shape rather than trust 200.
        if (!p || typeof p !== 'object' || !Array.isArray(p.bhajanIds)) {
          setPlaylistState('notfound');
          return;
        }
        setPlaylist(p);
        setPlaylistState('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        setPlaylistState(e?.response?.status === 404 ? 'notfound' : 'error');
      });
    return () => { cancelled = true; };
  }, [playlistId]);

  const allNodes = useMemo<AnyGraphNode[]>(
    () => (catalog?.nodes ?? []),
    [catalog]
  );

  // Resolve the playlist's bhajan ids against the in-memory catalog, preserving order.
  const bhajans = useMemo(() => {
    if (!playlist || !Array.isArray(playlist.bhajanIds)) return [];
    const byId = new Map(
      allNodes.filter((b) => b.nodeTypeId === BHAJAN_NODE_TYPE_ID).map((b) => [b.nodeId, b])
    );
    return playlist.bhajanIds
      .map((id) => byId.get(id))
      .filter((b): b is AnyGraphNode => !!b)
      .map((b) => bhajanToCardData(b, allNodes));
  }, [playlist, allNodes]);

  const catalogLoading = status === 'loading' || (status === 'idle' && !catalog);
  const loading = playlistState === 'loading' || (playlistState === 'ready' && catalogLoading);

  if (playlistState === 'notfound' || playlistState === 'error') {
    return (
      <main id="main">
        <EmptyState
          title={playlistState === 'notfound' ? 'Playlist not found' : "Couldn't load this playlist"}
          description={
            playlistState === 'notfound'
              ? 'This playlist link may be invalid or the playlist was removed.'
              : 'Something went wrong loading this playlist. Please try again.'
          }
          action={<Link to="/bhajans" className="text-accent-gold hover:underline">Browse all bhajans</Link>}
        />
      </main>
    );
  }

  return (
    <main id="main">
      <article className="mx-auto max-w-3xl px-4 sm:px-6 pt-8 sm:pt-12 pb-10">
        <header className="mb-6 text-center">
          <p className="text-small uppercase tracking-[0.2em] text-accent-gold/80 mb-1">Playlist</p>
          <h1 className="text-h1 sm:text-display font-display font-semibold leading-tight">
            {playlist?.name ?? <Skeleton width={280} height={40} />}
          </h1>
          <p className="mt-2 text-text-secondary">
            {loading ? 'Loading…' : `${bhajans.length} bhajan${bhajans.length === 1 ? '' : 's'}`}
          </p>
        </header>

        {/* Force-open the playlist in the native app (scheme/intent + store fallback). */}
        <div className="mb-8 flex justify-center">
          <button
            type="button"
            onClick={() => playlistId && openPlaylistInApp(playlistId)}
            className="inline-flex items-center gap-2 rounded-xl bg-accent-gold px-6 py-3 font-semibold text-bg-0 hover:brightness-105 transition"
          >
            Open this playlist in the Bhajan Mala app →
          </button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-light bg-bg-2 overflow-hidden">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-light/40 last:border-b-0">
                <Skeleton width={32} height={16} />
                <Skeleton width={200} height={20} />
              </div>
            ))}
          </div>
        ) : bhajans.length > 0 ? (
          <ol className="rounded-2xl border border-light bg-bg-2 overflow-hidden divide-y divide-white/5">
            {bhajans.map((b, i) => (
              <li key={`${b.nodeId}-${i}`}>
                <Link
                  to={`/nodes/${b.nodeId}`}
                  className="flex items-baseline gap-4 px-4 sm:px-5 py-3 hover:bg-bg-3/60 transition-colors group"
                >
                  <span className="font-mono text-small text-text-tertiary tabular-nums w-8 shrink-0 text-right">
                    {i + 1}.
                  </span>
                  <span className="flex-1 min-w-0 font-display text-h3 truncate group-hover:text-accent-gold transition-colors">
                    {b.title}
                  </span>
                  <span className="hidden sm:flex items-center gap-2 text-small text-text-tertiary shrink-0">
                    {b.ragam && <span>{b.ragam}</span>}
                    {b.talam && <span>·</span>}
                    {b.talam && <span>{b.talam}</span>}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        ) : (
          <EmptyState
            title="No bhajans in this playlist"
            description="This playlist doesn't have any bhajans yet."
          />
        )}

        {/* Get-the-app promo (same component as the home page) */}
        <AppDownload className="mt-10" />
      </article>
    </main>
  );
};

export default Playlist;
